import React, { useState, useEffect, useRef } from "react";
import { useList, useUpdate, useNotification, useNavigation } from "@refinedev/core";
import { 
  Card, 
  DatePicker, 
  Select, 
  Space, 
  Tag, 
  Button, 
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Spin,
  message,
  Checkbox,
  Row,
  Col,
  Divider,
  Empty,
  Alert,
  Tooltip,
  Descriptions,
  Table,
  Tabs,
  Statistic,
  Typography
} from "antd";

const { TextArea } = Input;
import {
  CalendarOutlined,
  ReloadOutlined,
  FilterOutlined,
  EditOutlined,
  CloseCircleOutlined,
  MessageOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  TeamOutlined,
  HomeOutlined,
  DragOutlined,
  AimOutlined,
  BankOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import "dayjs/locale/de";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale("de");

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

// Add styles for timeline scrollbars and drag effects
const timelineStyles = `
  .timeline-scroll-container {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
  }
  
  .timeline-scroll-container::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
  
  .timeline-top-scrollbar::-webkit-scrollbar,
  .timeline-bottom-scrollbar::-webkit-scrollbar {
    height: 12px;
  }
  
  .timeline-top-scrollbar::-webkit-scrollbar-track,
  .timeline-bottom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  .timeline-top-scrollbar::-webkit-scrollbar-thumb,
  .timeline-bottom-scrollbar::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 6px;
  }
  
  .timeline-top-scrollbar::-webkit-scrollbar-thumb:hover,
  .timeline-bottom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  @keyframes pulse {
    0% { opacity: 0.6; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.02); }
    100% { opacity: 0.6; transform: scale(1); }
  }

  .booking-block {
    transition: all 0.2s ease;
  }

  .booking-block:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.2) !important;
  }

  .booking-block.dragging {
    opacity: 0.5;
    transform: rotate(2deg);
  }
`;

// Inject styles
if (typeof document !== 'undefined' && !document.getElementById('timeline-styles')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'timeline-styles';
  styleEl.innerHTML = timelineStyles;
  document.head.appendChild(styleEl);
}

interface BookingData {
  booking_id: number;
  advert_id: number;
  title: string;
  start_date: string;
  end_date: string;
  status: string;
  status_code: number;
  source: string;
  source_color: string;
  guest: {
    name: string;
    email: string;
    phone: string;
    company?: string;
  };
  adults: number;
  children: number[] | null; // Array mit Altern der Kinder (kann null sein)
  dogs: number;
  invoices: any[];
  offers: any[];
  calculation: any;
  created_at: string;
  modified_at: string;
  is_draggable: boolean;
  platform_id: string;
  ical_uid: string;
}

interface AdvertData {
  id: number;
  title: string;
  shortTitle?: string;
  street: string;
  city: string;
  maxGuests: number;
  status: number;
}

interface TimelineResponse {
  adverts: AdvertData[];
  bookings: BookingData[];
  dateRange: {
    start: string;
    end: string;
  };
  statistics: {
    total_bookings: number;
    confirmed: number;
    pending: number;
    cancelled: number;
  };
}

const statusColors: Record<string, string> = {
  confirmed: '#52c41a',
  pending: '#faad14',
  cancelled: '#ff4d4f',
  rejected: '#ff7875',
  external: '#1890ff',
  blocked: '#8c8c8c',
  special: '#722ed1',
  unknown: '#d9d9d9'
};

const platformIcons: Record<string, string> = {
  'booking.com': '🅱️',
  'airbnb': '🏠',
  'direct': '✉️',
  'expedia': '🌐',
  'default': '📅'
};

const platformColors: Record<string, string> = {
  'booking.com': '#003580',
  'airbnb': '#FF5A5F',
  'direct': '#00617f',
  'expedia': '#FFC72C',
  'default': '#8c8c8c'
};

// Helper function to safely access children array
const safeChildren = (children: number[] | null | undefined): number[] => {
  return Array.isArray(children) ? children : [];
};

export const BookingTimeline: React.FC = () => {
  const { open } = useNotification();
  const { push } = useNavigation();
  const [selectedBooking, setSelectedBooking] = useState<BookingData | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(4, 'month'),
    dayjs().add(18, 'month')
  ]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [platformFilter, setPlatformFilter] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [timelineData, setTimelineData] = useState<TimelineResponse | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [draggedBooking, setDraggedBooking] = useState<BookingData | null>(null);
  const [dragPreviewPosition, setDragPreviewPosition] = useState<{x: number, y: number, advertId: number} | null>(null);
  const [dragSnapPosition, setDragSnapPosition] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<{startDate: dayjs.Dayjs, endDate?: dayjs.Dayjs, advertId: number} | null>(null);
  const [availabilityCheck, setAvailabilityCheck] = useState<{available: boolean, conflicts?: any[]} | null>(null);
  const [priceCalculation, setPriceCalculation] = useState<any>(null);
  const [bookingForm] = Form.useForm();
  const [drawerWidth, setDrawerWidth] = useState(window.innerWidth < 768 ? '100%' : 800);
  const [form] = Form.useForm();

  // Handle window resize for drawer width
  useEffect(() => {
    const handleResize = () => {
      setDrawerWidth(window.innerWidth < 768 ? '100%' : 800);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load timeline data
  const loadTimelineData = async () => {
    setLoading(true);
    try {
      const baseUrl = `/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=bookings-timeline`;
      const response = await fetch(
        `${baseUrl}&start_date=${dateRange[0].format('YYYY-MM-DD')}&end_date=${dateRange[1].format('YYYY-MM-DD')}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      setTimelineData(data);
    } catch (error) {
      open?.({
        type: "error",
        message: "Fehler beim Laden der Buchungsdaten",
        description: error instanceof Error ? error.message : "Unbekannter Fehler"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimelineData();
  }, [dateRange]);

  // Scroll to initial date after data is loaded
  useEffect(() => {
    if (timelineData) {
      // Use a timeout to ensure DOM is updated
      setTimeout(() => {
        const targetDate = getStartDateFromUrl();
        scrollToDate(targetDate);
      }, 100);
    }
  }, [timelineData]);

  // Filter bookings
  const filteredBookings = timelineData?.bookings.filter(booking => {
    if (statusFilter.length > 0 && !statusFilter.includes(booking.status)) {
      return false;
    }
    if (platformFilter.length > 0 && !platformFilter.includes(booking.source)) {
      return false;
    }
    return true;
  }) || [];

  // Group bookings by advert
  const bookingsByAdvert = filteredBookings.reduce((acc, booking) => {
    if (!acc[booking.advert_id]) {
      acc[booking.advert_id] = [];
    }
    acc[booking.advert_id].push(booking);
    return acc;
  }, {} as Record<number, BookingData[]>);

  // Calculate timeline scale
  const dayWidth = 40; // pixels per day - increased for day labels
  const totalDays = dateRange[1].diff(dateRange[0], 'day');
  const timelineWidth = totalDays * dayWidth;
  const leftColumnWidth = 70; // Fixed width for object names
  
  // Calculate today's position
  const todayPosition = dayjs().startOf('day').diff(dateRange[0].startOf('day'), 'day');

  // Generate day markers
  const generateDayMarkers = () => {
    const markers = [];
    const start = dateRange[0];
    const end = dateRange[1];
    let current = start.startOf('month');

    while (current.isSameOrBefore(end)) {
      markers.push({
        date: current,
        label: current.format('MMM YYYY'),
        position: current.diff(start, 'day') * dayWidth
      });
      current = current.add(1, 'month');
    }
    return markers;
  };

  // Generate individual days
  const generateDays = () => {
    const days = [];
    const start = dateRange[0];
    const end = dateRange[1];
    let current = start.clone();

    while (current.isSameOrBefore(end)) {
      days.push({
        date: current.clone(),
        dayOfWeek: current.format('dd'), // Mo, Di, Mi, etc.
        dayOfMonth: current.format('D'),
        isWeekend: current.day() === 0 || current.day() === 6,
        position: current.diff(start, 'day') * dayWidth
      });
      current = current.add(1, 'day');
    }
    return days;
  };

  const dayMarkers = generateDayMarkers();
  const days = generateDays();

  // Reset drag state
  const resetDragState = () => {
    setDraggedBooking(null);
    setDragPreviewPosition(null);
    setDragSnapPosition(null);
    setIsDragging(false);
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, booking: BookingData) => {
    if (!booking.is_draggable) {
      e.preventDefault();
      message.warning('Diese Buchung kann nicht verschoben werden');
      return;
    }
    
    setDraggedBooking(booking);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    
    // Create custom drag image with transparency
    const dragElement = e.currentTarget as HTMLElement;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx && dragElement) {
      canvas.width = dragElement.offsetWidth;
      canvas.height = dragElement.offsetHeight;
      ctx.globalAlpha = 0.7;
      e.dataTransfer.setDragImage(canvas, 0, 0);
    }
  };

  // Calculate snap position
  const calculateSnapPosition = (x: number, advertId: number): number => {
    const timelineContainer = document.querySelector('.timeline-scroll-container');
    if (!timelineContainer) return 0;
    
    const containerRect = timelineContainer.getBoundingClientRect();
    const relativeX = x - containerRect.left + timelineContainer.scrollLeft;
    
    // Snap to day boundaries
    const dayOffset = Math.round((relativeX - dayWidth / 2) / dayWidth);
    return Math.max(0, dayOffset);
  };

  // Handle drag over with snap preview
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (!draggedBooking || !isDragging) return;
    
    const advertElement = e.currentTarget as HTMLElement;
    const advertId = parseInt(advertElement.getAttribute('data-advert-id') || '0');
    
    // Calculate snap position
    const snapDayOffset = calculateSnapPosition(e.clientX, advertId);
    const snapPixelPosition = snapDayOffset * dayWidth + (dayWidth / 2);
    
    setDragSnapPosition(snapPixelPosition);
    setDragPreviewPosition({
      x: snapPixelPosition,
      y: e.clientY,
      advertId: advertId
    });
  };

  // Handle drop
  const handleDrop = async (e: React.DragEvent, advertId: number) => {
    e.preventDefault();
    
    if (!draggedBooking) return;
    
    // Calculate new dates based on drop position using snap position
    // Berücksichtigt die 12:00-12:00 Darstellung (Buchungen beginnen in der Mitte des Tages)
    const dayOffset = calculateSnapPosition(e.clientX, advertId);
    const newStartDate = dateRange[0].startOf('day').add(dayOffset, 'day');
    const draggedStart = dayjs(draggedBooking.start_date).startOf('day');
    const draggedEnd = dayjs(draggedBooking.end_date).startOf('day');
    const duration = draggedEnd.diff(draggedStart, 'day');
    const newEndDate = newStartDate.add(duration, 'day');

    // Check for conflicts (berücksichtigt 12:00-12:00 Regel)
    const conflictingBookings = bookingsByAdvert[advertId]?.filter(booking => {
      if (booking.booking_id === draggedBooking.booking_id) return false;
      
      const bookingStart = dayjs(booking.start_date).startOf('day');
      const bookingEnd = dayjs(booking.end_date).startOf('day');
      
      // Bei 12:00-12:00 Regel: An- und Abreise am gleichen Tag ist erlaubt
      // Überschneidung nur wenn sich Aufenthalte tatsächlich überschneiden
      
      // Erlaubt: newEnd == bookingStart (Abreise 12:00, Ankunft 12:00 am gleichen Tag)
      if (newEndDate.isSame(bookingStart)) return false;
      
      // Erlaubt: bookingEnd == newStart (bestehende Abreise 12:00, neue Ankunft 12:00)  
      if (bookingEnd.isSame(newStartDate)) return false;
      
      // Überschneidung prüfen
      return (
        // Neue Buchung beginnt vor Ende der bestehenden UND endet nach Beginn der bestehenden
        newStartDate.isBefore(bookingEnd) && newEndDate.isAfter(bookingStart)
      );
    });

    if (conflictingBookings && conflictingBookings.length > 0) {
      Modal.warning({
        title: 'Überschneidung erkannt',
        content: (
          <div>
            <p>Die Buchung überschneidet sich mit folgenden Buchungen:</p>
            <ul>
              {conflictingBookings.map(booking => (
                <li key={booking.booking_id}>
                  {booking.guest.name} ({dayjs(booking.start_date).format('DD.MM.YYYY')} - {dayjs(booking.end_date).format('DD.MM.YYYY')})
                </li>
              ))}
            </ul>
          </div>
        )
      });
      resetDragState();
      return;
    }

    // Confirm move
    Modal.confirm({
      title: 'Buchung verschieben',
      content: `Möchten Sie die Buchung von ${draggedBooking.guest.name} nach ${newStartDate.format('DD.MM.YYYY')} - ${newEndDate.format('DD.MM.YYYY')} verschieben?`,
      onOk: async () => {
        try {
          // TODO: Implement booking update endpoint in RestApiController
          // Update booking via API
          const response = await fetch(`/api/bookings/${draggedBooking.booking_id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              start_date: newStartDate.format('YYYY-MM-DD'),
              end_date: newEndDate.format('YYYY-MM-DD'),
              advert: advertId
            })
          });

          if (!response.ok) {
            throw new Error('Fehler beim Verschieben der Buchung');
          }

          message.success('Buchung erfolgreich verschoben');
          loadTimelineData(); // Reload data
        } catch (error) {
          message.error('Fehler beim Verschieben der Buchung');
        }
      }
    });

    resetDragState();
  };

  // Format guest name as "Nachname, Vorname"
  const formatGuestName = (name: string): string => {
    // If name already contains comma, assume it's already in correct format
    if (name.includes(',')) {
      return name;
    }
    
    // Split name by spaces and assume last word is surname
    const nameParts = name.trim().split(' ');
    if (nameParts.length < 2) {
      return name; // Return as-is if only one name part
    }
    
    const surname = nameParts.pop() || '';
    const firstname = nameParts.join(' ');
    return `${surname}, ${firstname}`;
  };

  // Render booking block
  const renderBooking = (booking: BookingData) => {
    // Normalisiere alle Daten auf den Beginn des Tages (00:00:00)
    const startDate = dayjs(booking.start_date).startOf('day');
    const endDate = dayjs(booking.end_date).startOf('day');
    const rangeStart = dateRange[0].startOf('day');
    
    // Buchungen gehen von 12:00 Anreisetag bis 12:00 Abreisetag
    // Optische Darstellung: Start in der Mitte des Anreisetages
    const daysSinceStart = startDate.diff(rangeStart, 'day');
    const left = daysSinceStart * dayWidth + (dayWidth / 2);
    const width = endDate.diff(startDate, 'day') * dayWidth;

    return (
      <div
        key={booking.booking_id}
        className={`booking-block ${isDragging && draggedBooking?.booking_id === booking.booking_id ? 'dragging' : ''}`}
        style={{
          position: 'absolute',
          left: `${left}px`,
          width: `${width}px`,
          backgroundColor: statusColors[booking.status],
          height: '40px',
          borderRadius: '4px',
          cursor: booking.is_draggable ? 'move' : 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          color: '#fff',
          fontSize: '12px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          border: '1px solid rgba(0,0,0,0.1)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          opacity: isDragging && draggedBooking?.booking_id === booking.booking_id ? 0.5 : 1,
          zIndex: isDragging && draggedBooking?.booking_id === booking.booking_id ? 7 : 3
        }}
        draggable={booking.is_draggable}
        onDragStart={(e) => handleDragStart(e, booking)}
        onDragEnd={resetDragState}
        onClick={() => {
          setSelectedBooking(booking);
          setDrawerVisible(true);
        }}
      >
        <span style={{ marginRight: '4px' }}>
          {booking.source.toLowerCase().includes('booking.com') ? 
            <span style={{ color: '#003580', fontWeight: 'bold', fontSize: '10px' }}>B</span> :
            (platformIcons[booking.source] || platformIcons.default)
          }
        </span>
        <span>{formatGuestName(booking.guest.name)}</span>
        <span style={{ 
          marginLeft: '4px',
          fontSize: '10px',
          color: 'rgba(255,255,255,0.9)',
          fontWeight: 'normal'
        }}>
          ({booking.adults + safeChildren(booking.children).length}P{booking.dogs > 0 ? ` +${booking.dogs}H` : ''})
        </span>
        {booking.is_draggable && (
          <DragOutlined style={{ marginLeft: 'auto', fontSize: '10px' }} />
        )}
      </div>
    );
  };

  // Render timeline header with dual scrollbar
  const renderTimelineHeader = (scrollLeft: number) => (
    <div style={{ 
      position: 'sticky', 
      top: 0, 
      backgroundColor: '#fff', 
      zIndex: 10,
      borderBottom: '1px solid #f0f0f0'
    }}>
      {/* Top scrollbar */}
      <div
        style={{
          overflow: 'auto',
          height: '17px',
          marginLeft: `${leftColumnWidth}px`,
          backgroundColor: '#f5f5f5'
        }}
        onScroll={(e) => {
          const container = document.querySelector('.timeline-scroll-container') as HTMLElement;
          if (container) {
            container.scrollLeft = e.currentTarget.scrollLeft;
          }
          setScrollLeft(e.currentTarget.scrollLeft);
        }}
        className="timeline-top-scrollbar"
      >
        <div style={{ width: `${timelineWidth}px`, height: '1px' }} />
      </div>

      {/* Month headers */}
      <div style={{ display: 'flex', height: '30px', backgroundColor: '#fff' }}>
        <div style={{ 
          width: `${leftColumnWidth}px`, 
          padding: '0 8px', 
          fontWeight: 'bold', 
          display: 'flex', 
          alignItems: 'center',
          fontSize: '11px',
          position: 'sticky',
          left: 0,
          zIndex: 11,
          backgroundColor: '#fff',
          borderRight: '2px solid #e0e0e0',
          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
        }}>
          Objekte
        </div>
        <div style={{ 
          overflow: 'hidden',
          position: 'relative',
          width: `calc(100% - ${leftColumnWidth}px)`
        }}>
          <div style={{ 
            position: 'relative', 
            width: `${timelineWidth}px`, 
            height: '100%',
            transform: `translateX(-${scrollLeft}px)`
          }}>
            {dayMarkers.map((marker, index) => (
              <div
                key={index}
                style={{
                  position: 'absolute',
                  left: `${marker.position}px`,
                  top: '5px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                {marker.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Day headers */}
      <div style={{ display: 'flex', height: '40px', backgroundColor: '#fafafa', borderTop: '1px solid #f0f0f0' }}>
        <div style={{ 
          width: `${leftColumnWidth}px`, 
          borderRight: '2px solid #e0e0e0',
          position: 'sticky',
          left: 0,
          zIndex: 11,
          backgroundColor: '#fafafa',
          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
        }} />
        <div style={{ 
          overflow: 'hidden',
          position: 'relative',
          width: `calc(100% - ${leftColumnWidth}px)`
        }}>
          <div style={{ 
            position: 'relative', 
            width: `${timelineWidth}px`, 
            height: '100%',
            transform: `translateX(-${scrollLeft}px)`
          }}>
                          {days.map((day, index) => {
                const isToday = index === todayPosition;
                return (
                  <div
                    key={index}
                    style={{
                      position: 'absolute',
                      left: `${day.position}px`,
                      width: `${dayWidth}px`,
                      height: '100%',
                      borderRight: '1px solid #e0e0e0',
                      backgroundColor: isToday ? '#ffe7e7' : (day.isWeekend ? '#f5f5f5' : '#fff'),
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px'
                    }}
                  >
                    <div style={{ 
                      fontWeight: isToday ? 'bold' : 'bold', 
                      color: isToday ? '#ff4d4f' : (day.isWeekend ? '#8b8b8b' : '#333') 
                    }}>
                      {day.dayOfWeek}
                    </div>
                    <div style={{ 
                      color: isToday ? '#ff4d4f' : (day.isWeekend ? '#8b8b8b' : '#666'),
                      fontWeight: isToday ? 'bold' : 'normal'
                    }}>
                      {day.dayOfMonth}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );

  // Render timeline
  const [scrollLeft, setScrollLeft] = useState(0);
  
  // Function to scroll to a specific date
  const scrollToDate = (targetDate: dayjs.Dayjs) => {
    const daysDiff = targetDate.startOf('day').diff(dateRange[0].startOf('day'), 'day');
    if (daysDiff >= 0 && daysDiff <= totalDays) {
      const scrollPosition = daysDiff * dayWidth;
      
      // Scroll all timeline containers
      const containers = [
        document.querySelector('.timeline-scroll-container'),
        document.querySelector('.timeline-top-scrollbar'),
        document.querySelector('.timeline-bottom-scrollbar')
      ];
      
      containers.forEach(container => {
        if (container) {
          (container as HTMLElement).scrollLeft = scrollPosition;
        }
      });
      
      setScrollLeft(scrollPosition);
    }
  };

  // Function to scroll to today
  const scrollToToday = () => {
    scrollToDate(dayjs());
  };

  // Get start date from URL parameters
  const getStartDateFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const startDateParam = urlParams.get('startDate');
    if (startDateParam) {
      const parsedDate = dayjs(startDateParam);
      if (parsedDate.isValid()) {
        return parsedDate;
      }
    }
    return dayjs(); // Default to today
  };

  // Create invoice from booking
  const handleCreateInvoice = (booking: BookingData) => {
    const invoiceData = {
      // Booking reference
      booking_id: booking.booking_id,
      booking_title: booking.title,
      
      // Guest information
      guest_name: booking.guest.name,
      guest_email: booking.guest.email,
      guest_phone: booking.guest.phone,
      guest_company: booking.guest.company || '',
      
      // Booking details
      check_in: booking.start_date,
      check_out: booking.end_date,
      adults: booking.adults,
      children: safeChildren(booking.children), // Array mit Altern (sicher)
      children_count: safeChildren(booking.children).length,
      dogs: booking.dogs,
      
      // Advert information
      advert_id: booking.advert_id,
      advert_title: booking.title,
      
      // Calculation data
      calculation: booking.calculation,
      
      // Pre-fill invoice items based on calculation
      items: booking.calculation ? generateInvoiceItemsFromCalculation(booking.calculation, booking) : []
    };

    // Navigate to invoice creation with pre-filled data
    const encodedData = encodeURIComponent(JSON.stringify(invoiceData));
    window.location.hash = `#/rechnungen/neu?booking=${booking.booking_id}&prefill=${encodedData}`;
  };

  // Generate invoice items from booking calculation
  const generateInvoiceItemsFromCalculation = (calculation: any, booking: BookingData) => {
    const items = [];
    
    // Base accommodation
    if (calculation.accommodation) {
      items.push({
        description: `Unterkunft: ${booking.title}`,
        quantity: dayjs(booking.end_date).diff(dayjs(booking.start_date), 'day'),
        unit: 'Nacht',
        price: calculation.accommodation.price_per_night || 0,
        total: calculation.accommodation.total || 0
      });
    }

    // Additional services
    if (calculation.additional_services && Array.isArray(calculation.additional_services)) {
      calculation.additional_services.forEach((service: any) => {
        items.push({
          description: service.name || service.description,
          quantity: service.quantity || 1,
          unit: service.unit || 'Stück',
          price: service.price || 0,
          total: service.total || service.price
        });
      });
    }

    // Cleaning fee
    if (calculation.cleaning_fee) {
      items.push({
        description: 'Endreinigung',
        quantity: 1,
        unit: 'Pauschal',
        price: calculation.cleaning_fee,
        total: calculation.cleaning_fee
      });
    }

    // Tourist tax
    if (calculation.tourist_tax) {
      items.push({
        description: 'Kurtaxe',
        quantity: (booking.adults + safeChildren(booking.children).length),
        unit: 'Person',
        price: calculation.tourist_tax.per_person || 0,
        total: calculation.tourist_tax.total || 0
      });
    }

    return items;
  };

  // Create offer from booking (similar to invoice)
  const handleCreateOffer = (booking: BookingData) => {
    const offerData = {
      // Booking reference
      booking_id: booking.booking_id,
      booking_title: booking.title,
      
      // Guest information
      guest_name: booking.guest.name,
      guest_email: booking.guest.email,
      guest_phone: booking.guest.phone,
      guest_company: booking.guest.company || '',
      
      // Booking details
      check_in: booking.start_date,
      check_out: booking.end_date,
      adults: booking.adults,
      children: safeChildren(booking.children), // Array mit Altern (sicher)
      children_count: safeChildren(booking.children).length,
      dogs: booking.dogs,
      
      // Advert information
      advert_id: booking.advert_id,
      advert_title: booking.title,
      
      // Calculation data
      calculation: booking.calculation,
      
      // Pre-fill offer items based on calculation
      items: booking.calculation ? generateInvoiceItemsFromCalculation(booking.calculation, booking) : [],
      
      // Set as offer type
      type: 'offer'
    };

    // Navigate to invoice creation (which handles both invoices and offers) with pre-filled data
    const encodedData = encodeURIComponent(JSON.stringify(offerData));
    window.location.hash = `#/rechnungen/neu?booking=${booking.booking_id}&type=offer&prefill=${encodedData}`;
  };

  // Handle day click to create new booking
  const handleDayClick = (dayOffset: number, advertId: number) => {
    const clickedDate = dateRange[0].startOf('day').add(dayOffset, 'day');
    
    // Don't allow booking in the past
    if (clickedDate.isBefore(dayjs().startOf('day'))) {
      message.warning('Buchungen können nicht in der Vergangenheit erstellt werden');
      return;
    }

    setSelectedDateRange({
      startDate: clickedDate,
      endDate: clickedDate.add(1, 'day'), // Default: 1 night
      advertId: advertId
    });
    
    // Reset form and states
    bookingForm.resetFields();
    setAvailabilityCheck(null);
    setPriceCalculation(null);
    setBookingModalVisible(true);

    // Set initial form values
    bookingForm.setFieldsValue({
      checkIn: clickedDate,
      checkOut: clickedDate.add(1, 'day'),
      adults: 2,
      children: [],
      dogs: 0
    });
  };

  // Check availability for selected dates and advert
  const checkAvailability = async (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs, advertId: number) => {
    try {
      const response = await fetch(
        `/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=bookings-check-availability&advert=${advertId}&start_date=${startDate.format('YYYY-MM-DD')}&end_date=${endDate.format('YYYY-MM-DD')}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );
      
      const data = await response.json();
      setAvailabilityCheck(data);
      return data;
    } catch (error) {
      message.error('Fehler bei der Verfügbarkeitsprüfung');
      return null;
    }
  };

  // Calculate price for booking
  const calculatePrice = async (formData: any) => {
    try {
      // Convert birth years to ages for the API
      const childrenAges = formData.children ? formData.children.map((birthYear: number) => {
        return new Date().getFullYear() - birthYear;
      }) : [];

      const response = await fetch(
        `/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=bookings-calculate`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            advert: selectedDateRange?.advertId,
            start_date: formData.checkIn.format('YYYY-MM-DD'),
            end_date: formData.checkOut.format('YYYY-MM-DD'),
            guests: formData.adults,
            children: childrenAges,
            pets: formData.dogs || 0
          })
        }
      );
      
      const data = await response.json();
      setPriceCalculation(data);
      return data;
    } catch (error) {
      message.error('Fehler bei der Preisberechnung');
      return null;
    }
  };

  // Handle form value changes
  const handleBookingFormChange = async (changedValues: any, allValues: any) => {
    if (changedValues.checkIn || changedValues.checkOut) {
      const { checkIn, checkOut } = allValues;
      if (checkIn && checkOut && selectedDateRange) {
        // Update selected date range
        setSelectedDateRange({
          ...selectedDateRange,
          startDate: checkIn,
          endDate: checkOut
        });

        // Check availability
        await checkAvailability(checkIn, checkOut, selectedDateRange.advertId);
      }
    }

    // Calculate price when relevant fields change
    if (allValues.checkIn && allValues.checkOut && allValues.adults) {
      await calculatePrice(allValues);
    }
  };

  // Create new booking
  const handleCreateBooking = async (formData: any) => {
    try {
      // Final availability check
      const availability = await checkAvailability(
        formData.checkIn, 
        formData.checkOut, 
        selectedDateRange!.advertId
      );

      if (!availability?.available) {
        message.error('Der gewählte Zeitraum ist nicht mehr verfügbar');
        return;
      }

      // Create booking via API
      const bookingData = {
        advert_id: selectedDateRange!.advertId,
        start_date: formData.checkIn.format('YYYY-MM-DD'),
        end_date: formData.checkOut.format('YYYY-MM-DD'),
        adults: formData.adults,
        children: safeChildren(formData.children),
        dogs: formData.dogs || 0,
        guest_name: formData.guestName,
        guest_email: formData.guestEmail,
        guest_phone: formData.guestPhone,
        special_requests: formData.specialRequests
      };

      const response = await fetch(
        '/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=bookings-create',
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(bookingData)
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        message.success('Buchung erfolgreich erstellt');
        setBookingModalVisible(false);
        loadTimelineData(); // Reload timeline to show new booking
      } else {
        throw new Error(result.error || 'Unbekannter Fehler');
      }

    } catch (error) {
      message.error('Fehler beim Erstellen der Buchung: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
    }
  };

  // Generate Gästemappe link
  const generateGuestemappeLink = (booking: BookingData): string => {
    if (!booking.guest.email || !booking.start_date || !booking.end_date) {
      return '#'; // Fallback if required data is missing
    }
    
    const email = booking.guest.email;
    const arrival = dayjs(booking.start_date).format('YYYY-MM-DD');
    const departure = dayjs(booking.end_date).format('YYYY-MM-DD');
    
    // Combine email, arrival, departure and base64 encode
    const combinedData = `${email},${arrival},${departure}`;
    const encodedData = btoa(combinedData); // Base64 encode
    
    return `/gaestemappe?${encodedData}`;
  };
  
  const renderTimeline = () => {
    if (!timelineData) return null;

    return (
      <div style={{ 
        position: 'relative',
        height: 'calc(100vh - 350px)', // Max viewport height minus header and stats
        display: 'flex',
        flexDirection: 'column'
      }}>
        {renderTimelineHeader(scrollLeft)}

        {/* Content area with fixed left column and scrollable right */}
        <div style={{ 
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          display: 'flex'
        }}>
          {/* Fixed left column with advert names */}
          <div style={{
            width: `${leftColumnWidth}px`,
            backgroundColor: '#fafafa',
            borderRight: '2px solid #e0e0e0',
            boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
            zIndex: 10,
            overflow: 'hidden'
          }}>
            {timelineData.adverts.map((advert) => (
              <div
                key={`header-${advert.id}`}
                style={{
                  height: '60px',
                  borderBottom: '1px solid #f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px'
                }}
              >
                <Tooltip 
                  title={
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{advert.title}</div>
                      <div style={{ fontSize: '12px' }}>
                        <HomeOutlined style={{ marginRight: 4 }} />
                        {advert.street}, {advert.city}
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        <TeamOutlined style={{ marginRight: 4 }} />
                        Max. {advert.maxGuests} Gäste
                      </div>
                    </div>
                  }
                  placement="right"
                >
                  <div style={{ 
                    fontWeight: 'bold', 
                    fontSize: '11px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                    width: '100%',
                    cursor: 'help'
                  }}>
                    {advert.shortTitle || advert.title}
                  </div>
                </Tooltip>
              </div>
            ))}
          </div>

          {/* Scrollable timeline content */}
          <div 
            className="timeline-scroll-container"
            style={{ 
              flex: 1,
              height: '100%',
              overflow: 'auto',
              position: 'relative'
            }}
            onScroll={(e) => {
              setScrollLeft(e.currentTarget.scrollLeft);
              // Sync top scrollbar
              const topScrollbar = document.querySelector('.timeline-top-scrollbar') as HTMLElement;
              if (topScrollbar) {
                topScrollbar.scrollLeft = e.currentTarget.scrollLeft;
              }
              // Sync bottom scrollbar
              const bottomScrollbar = document.querySelector('.timeline-bottom-scrollbar') as HTMLElement;
              if (bottomScrollbar) {
                bottomScrollbar.scrollLeft = e.currentTarget.scrollLeft;
              }
            }}
          >
            {/* Advert rows - only booking lanes */}
            {timelineData.adverts.map((advert) => (
                             <div 
                 key={advert.id} 
                 style={{ 
                   borderBottom: '1px solid #f0f0f0',
                   height: '60px',
                   position: 'relative',
                   backgroundColor: isDragging && dragPreviewPosition?.advertId === advert.id ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
                   transition: 'background-color 0.2s ease'
                 }}
                 data-advert-id={advert.id}
                 onDragOver={handleDragOver}
                 onDrop={(e) => handleDrop(e, advert.id)}
              >
                                  {/* Day backgrounds with weekend highlighting and weekday labels */}
                  {days.map((day, index) => {
                    const isToday = index === todayPosition;
                    return (
                      <div
                        key={`bg-${index}`}
                        style={{
                          position: 'absolute',
                          left: `${day.position}px`,
                          width: `${dayWidth}px`,
                          top: 0,
                          bottom: 0,
                          backgroundColor: isToday ? '#ffe7e7' : (day.isWeekend ? '#fafafa' : 'transparent'),
                          borderRight: '1px solid #e0e0e0',
                          cursor: !isDragging ? 'pointer' : 'default'
                        }}
                        onClick={() => !isDragging && handleDayClick(index, advert.id)}
                        onMouseEnter={(e) => {
                          if (!isDragging) {
                            e.currentTarget.style.backgroundColor = isToday ? '#ffd6d6' : (day.isWeekend ? '#f0f0f0' : '#f9f9f9');
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isDragging) {
                            e.currentTarget.style.backgroundColor = isToday ? '#ffe7e7' : (day.isWeekend ? '#fafafa' : 'transparent');
                          }
                        }}
                      >
                        <div style={{
                          position: 'absolute',
                          bottom: '2px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '9px',
                          color: isToday ? '#ff4d4f' : '#d0d0d0',
                          userSelect: 'none',
                          fontWeight: isToday ? 'bold' : 'normal',
                          pointerEvents: 'none'
                        }}>
                          {day.dayOfWeek}
                        </div>
                      </div>
                    );
                  })}
                
                                 {/* Snap preview indicator */}
                 {isDragging && dragPreviewPosition?.advertId === advert.id && dragSnapPosition !== null && (
                   <div
                     style={{
                       position: 'absolute',
                       left: `${dragSnapPosition}px`,
                       top: '15px',
                       height: '30px',
                       width: draggedBooking ? `${dayjs(draggedBooking.end_date).startOf('day').diff(dayjs(draggedBooking.start_date).startOf('day'), 'day') * dayWidth}px` : '40px',
                       backgroundColor: 'rgba(24, 144, 255, 0.3)',
                       border: '2px dashed #1890ff',
                       borderRadius: '4px',
                       zIndex: 6,
                       pointerEvents: 'none',
                       animation: 'pulse 1s infinite'
                     }}
                   />
                 )}

                 {/* Bookings */}
                 <div style={{ position: 'relative', padding: '10px 0' }}>
                   {bookingsByAdvert[advert.id]?.map(booking => renderBooking(booking))}
                 </div>
              </div>
            ))}

            {/* Today marker */}
            <div
              style={{
                position: 'absolute',
                left: `${dayjs().startOf('day').diff(dateRange[0].startOf('day'), 'day') * dayWidth}px`,
                top: '0',
                bottom: 0,
                width: '2px',
                backgroundColor: '#ff4d4f',
                zIndex: 8,
                pointerEvents: 'none'
              }}
            >
              <div style={{
                position: 'absolute',
                top: '-20px',
                left: '-20px',
                backgroundColor: '#ff4d4f',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                whiteSpace: 'nowrap'
              }}>
                Heute
              </div>
            </div>
          </div>
        </div>

        {/* Bottom scrollbar */}
        <div
          style={{
            overflow: 'auto',
            height: '17px',
            marginLeft: `${leftColumnWidth}px`,
            backgroundColor: '#f5f5f5',
            borderTop: '1px solid #e0e0e0'
          }}
          onScroll={(e) => {
            const container = document.querySelector('.timeline-scroll-container') as HTMLElement;
            if (container) {
              container.scrollLeft = e.currentTarget.scrollLeft;
            }
            // Sync top scrollbar
            const topScrollbar = document.querySelector('.timeline-top-scrollbar') as HTMLElement;
            if (topScrollbar) {
              topScrollbar.scrollLeft = e.currentTarget.scrollLeft;
            }
            setScrollLeft(e.currentTarget.scrollLeft);
          }}
          className="timeline-bottom-scrollbar"
        >
          <div style={{ width: `${timelineWidth}px`, height: '1px' }} />
        </div>
      </div>
    );
  };

  return (
    <Card
      title="Buchungskalender"
      extra={
        <Space>
          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              if (dates) {
                setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs]);
              }
            }}
            format="DD.MM.YYYY"
          />
          <Select
            mode="multiple"
            placeholder="Status filtern"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ minWidth: 150 }}
            options={[
              { label: 'Bestätigt', value: 'confirmed' },
              { label: 'Ausstehend', value: 'pending' },
              { label: 'Storniert', value: 'cancelled' },
              { label: 'Extern', value: 'external' },
              { label: 'Blockiert', value: 'blocked' }
            ]}
          />
          <Select
            mode="multiple"
            placeholder="Plattform filtern"
            value={platformFilter}
            onChange={setPlatformFilter}
            style={{ minWidth: 150 }}
            options={[
              { label: 'Direkt', value: 'direct' },
              { label: 'Booking.com', value: 'booking.com' },
              { label: 'Airbnb', value: 'airbnb' },
              { label: 'Expedia', value: 'expedia' }
            ]}
          />
          <Button icon={<AimOutlined />} onClick={scrollToToday}>
            Heute
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadTimelineData}>
            Aktualisieren
          </Button>
        </Space>
      }
      bodyStyle={{ padding: '16px', overflow: 'hidden' }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : timelineData?.adverts.length === 0 ? (
        <Empty description="Keine Objekte gefunden" />
      ) : (
        <>
          {/* Statistics */}
          {timelineData && (
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                      {timelineData.statistics.total_bookings}
                    </div>
                    <div style={{ color: '#666' }}>Gesamt</div>
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                      {timelineData.statistics.confirmed}
                    </div>
                    <div style={{ color: '#666' }}>Bestätigt</div>
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
                      {timelineData.statistics.pending}
                    </div>
                    <div style={{ color: '#666' }}>Ausstehend</div>
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                      {timelineData.statistics.cancelled}
                    </div>
                    <div style={{ color: '#666' }}>Storniert</div>
                  </div>
                </Card>
              </Col>
            </Row>
          )}

          {/* Timeline */}
          <div ref={timelineRef}>
            {renderTimeline()}
          </div>
        </>
      )}

      {/* Booking Detail Drawer */}
      <Drawer
        title={`Buchung ${selectedBooking?.booking_id}`}
        placement="right"
        width={drawerWidth}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedBooking(null);
        }}
        open={drawerVisible}
      >
        {selectedBooking && (
          <Tabs defaultActiveKey="1">
            <TabPane tab="Details" key="1">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12}>
                  <Card title="Gastdaten" size="small" style={{ marginBottom: 16 }}>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Name">
                        <Space>
                          <UserOutlined />
                          <strong>{selectedBooking.guest.name}</strong>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="E-Mail">
                        <Space>
                          <MailOutlined />
                          <a href={`mailto:${selectedBooking.guest.email}`}>
                            {selectedBooking.guest.email}
                          </a>
                        </Space>
                      </Descriptions.Item>
                      {selectedBooking.guest.phone && (
                        <Descriptions.Item label="Telefon">
                          <Space>
                            <PhoneOutlined />
                            <a href={`tel:${selectedBooking.guest.phone}`}>
                              {selectedBooking.guest.phone}
                            </a>
                          </Space>
                        </Descriptions.Item>
                      )}
                      {selectedBooking.guest.company && (
                        <Descriptions.Item label="Firma">
                          <Space>
                            <BankOutlined />
                            {selectedBooking.guest.company}
                          </Space>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                    
                    {/* Gästemappe-Link */}
                    <Divider />
                    <Button 
                      type="link" 
                      icon={<UserOutlined />} 
                      size="small"
                      href={generateGuestemappeLink(selectedBooking)}
                      target="_blank"
                      rel="noopener noreferrer"
                      disabled={!selectedBooking.guest.email || !selectedBooking.start_date || !selectedBooking.end_date}
                    >
                      Zur aktiven Gästemappe
                    </Button>
                  </Card>
                </Col>

                <Col xs={24} sm={24} md={12}>
                  <Card title="Buchungsdetails" size="small" style={{ marginBottom: 16 }}>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Buchungs-ID">
                        #{selectedBooking.booking_id}
                      </Descriptions.Item>
                      <Descriptions.Item label="Zeitraum">
                        <Space>
                          <CalendarOutlined />
                          {dayjs(selectedBooking.start_date).format('DD.MM.YYYY')} - {dayjs(selectedBooking.end_date).format('DD.MM.YYYY')}
                        </Space>
                        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                          {dayjs(selectedBooking.end_date).diff(dayjs(selectedBooking.start_date), 'day')} Nächte
                        </div>
                      </Descriptions.Item>
                      <Descriptions.Item label="Personen">
                        <Space>
                          <TeamOutlined />
                          {selectedBooking.adults} Erwachsene
                          {safeChildren(selectedBooking.children).length > 0 && `, ${safeChildren(selectedBooking.children).length} Kinder (Alter: ${safeChildren(selectedBooking.children).join(', ')})`}
                          {selectedBooking.dogs > 0 && `, ${selectedBooking.dogs} Hunde`}
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="Status">
                        <Tag color={statusColors[selectedBooking.status]}>
                          {selectedBooking.status}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Plattform">
                        <Tag color={selectedBooking.source_color || platformColors[selectedBooking.source] || platformColors.default}>
                          {selectedBooking.source}
                        </Tag>
                      </Descriptions.Item>
                      {selectedBooking.ical_uid && (
                        <Descriptions.Item label="iCal UID">
                          <Typography.Text code style={{ fontSize: '11px' }}>
                            {selectedBooking.ical_uid}
                          </Typography.Text>
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="Erstellt">
                        {dayjs(selectedBooking.created_at).format('DD.MM.YYYY HH:mm')}
                      </Descriptions.Item>
                      {selectedBooking.modified_at !== selectedBooking.created_at && (
                        <Descriptions.Item label="Geändert">
                          {dayjs(selectedBooking.modified_at).format('DD.MM.YYYY HH:mm')}
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>
                </Col>
              </Row>

              <Divider />

              <Space direction="vertical" style={{ width: '100%' }}>
                <Button type="primary" icon={<EditOutlined />} block>
                  Buchung bearbeiten
                </Button>
                <Button danger icon={<CloseCircleOutlined />} block>
                  Buchung stornieren
                </Button>
                <Button icon={<MessageOutlined />} block>
                  Nachricht an Gast
                </Button>
              </Space>
            </TabPane>

            <TabPane tab="Dokumente" key="2">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Card title="Rechnungen" size="small">
                  {selectedBooking.invoices.length > 0 ? (
                    <Table
                      dataSource={selectedBooking.invoices}
                      columns={[
                        { title: 'Nummer', dataIndex: 'number', key: 'number' },
                        { title: 'Datum', dataIndex: 'date', key: 'date' },
                        { title: 'Status', dataIndex: 'status', key: 'status' }
                      ]}
                      pagination={false}
                      size="small"
                    />
                  ) : (
                    <Empty description="Keine Rechnungen vorhanden" />
                  )}
                  <Button 
                    type="primary" 
                    icon={<FileTextOutlined />} 
                    style={{ marginTop: 8 }}
                    onClick={() => selectedBooking && handleCreateInvoice(selectedBooking)}
                  >
                    Rechnung erstellen
                  </Button>
                </Card>

                <Card title="Angebote" size="small">
                  {selectedBooking.offers.length > 0 ? (
                    <Table
                      dataSource={selectedBooking.offers}
                      columns={[
                        { title: 'Nummer', dataIndex: 'number', key: 'number' },
                        { title: 'Datum', dataIndex: 'date', key: 'date' },
                        { title: 'Status', dataIndex: 'status', key: 'status' }
                      ]}
                      pagination={false}
                      size="small"
                    />
                  ) : (
                    <Empty description="Keine Angebote vorhanden" />
                  )}
                  <Button 
                    icon={<FileDoneOutlined />} 
                    style={{ marginTop: 8 }}
                    onClick={() => selectedBooking && handleCreateOffer(selectedBooking)}
                  >
                    Angebot erstellen
                  </Button>
                </Card>
              </Space>
            </TabPane>

            <TabPane tab="Kalkulation" key="3">
              {selectedBooking.calculation ? (
                <div>
                  {/* Accommodation costs */}
                  {selectedBooking.calculation.accommodation && (
                    <Card title="Unterkunft" size="small" style={{ marginBottom: 16 }}>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Statistic 
                            title="Preis pro Nacht" 
                            value={selectedBooking.calculation.accommodation.price_per_night || 0} 
                            suffix="€" 
                            precision={2}
                          />
                        </Col>
                        <Col xs={24} sm={12}>
                          <Statistic 
                            title="Anzahl Nächte" 
                            value={dayjs(selectedBooking.end_date).diff(dayjs(selectedBooking.start_date), 'day')} 
                          />
                        </Col>
                        <Col span={24} style={{ marginTop: 16 }}>
                          <Statistic 
                            title="Unterkunft gesamt" 
                            value={selectedBooking.calculation.accommodation.total || 0} 
                            suffix="€" 
                            precision={2}
                            valueStyle={{ color: '#1890ff', fontSize: '20px', fontWeight: 'bold' }}
                          />
                        </Col>
                      </Row>
                    </Card>
                  )}

                  {/* Additional services */}
                  {selectedBooking.calculation.additional_services && Array.isArray(selectedBooking.calculation.additional_services) && selectedBooking.calculation.additional_services.length > 0 && (
                    <Card title="Zusatzleistungen" size="small" style={{ marginBottom: 16 }}>
                      <Table
                        dataSource={selectedBooking.calculation.additional_services}
                        columns={[
                          { title: 'Leistung', dataIndex: 'name', key: 'name' },
                          { title: 'Menge', dataIndex: 'quantity', key: 'quantity', render: (val: number) => val || 1 },
                          { title: 'Einheit', dataIndex: 'unit', key: 'unit', render: (val: string) => val || 'Stück' },
                          { title: 'Preis', dataIndex: 'price', key: 'price', render: (val: number) => `${val?.toFixed(2) || '0.00'} €` },
                          { title: 'Gesamt', dataIndex: 'total', key: 'total', render: (val: number) => `${val?.toFixed(2) || '0.00'} €` }
                        ]}
                        pagination={false}
                        size="small"
                      />
                    </Card>
                  )}

                  {/* Fees */}
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    {selectedBooking.calculation.cleaning_fee && (
                      <Col xs={24} sm={8}>
                        <Card size="small">
                          <Statistic 
                            title="Endreinigung" 
                            value={selectedBooking.calculation.cleaning_fee} 
                            suffix="€" 
                            precision={2}
                          />
                        </Card>
                      </Col>
                    )}
                    
                    {selectedBooking.calculation.tourist_tax && (
                      <Col xs={24} sm={8}>
                        <Card size="small">
                          <Statistic 
                            title="Kurtaxe" 
                            value={selectedBooking.calculation.tourist_tax.total || 0} 
                            suffix="€" 
                            precision={2}
                          />
                          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                            {selectedBooking.calculation.tourist_tax.per_person?.toFixed(2)} € × {selectedBooking.adults + safeChildren(selectedBooking.children).length} Personen
                          </Typography.Text>
                        </Card>
                      </Col>
                    )}

                    {selectedBooking.calculation.pet_fee && (
                      <Col xs={24} sm={8}>
                        <Card size="small">
                          <Statistic 
                            title="Haustiergebühr" 
                            value={selectedBooking.calculation.pet_fee} 
                            suffix="€" 
                            precision={2}
                          />
                        </Card>
                      </Col>
                    )}
                  </Row>

                  {/* Tax information */}
                  {selectedBooking.calculation.taxes && (
                    <Card title="Steuern" size="small" style={{ marginBottom: 16 }}>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={8}>
                          <Statistic 
                            title="Nettobetrag" 
                            value={selectedBooking.calculation.taxes.net_amount || 0} 
                            suffix="€" 
                            precision={2}
                          />
                        </Col>
                        <Col xs={24} sm={8}>
                          <Statistic 
                            title={`MwSt. (${selectedBooking.calculation.taxes.vat_rate || 0}%)`}
                            value={selectedBooking.calculation.taxes.vat_amount || 0} 
                            suffix="€" 
                            precision={2}
                          />
                        </Col>
                        <Col xs={24} sm={8}>
                          <Statistic 
                            title="Bruttobetrag" 
                            value={selectedBooking.calculation.taxes.gross_amount || 0} 
                            suffix="€" 
                            precision={2}
                            valueStyle={{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }}
                          />
                        </Col>
                      </Row>
                    </Card>
                  )}

                  {/* Total */}
                  {selectedBooking.calculation.total && (
                    <Card size="small">
                      <Statistic 
                        title="Gesamtbetrag" 
                        value={selectedBooking.calculation.total} 
                        suffix="€" 
                        precision={2}
                        valueStyle={{ color: '#f5222d', fontSize: '24px', fontWeight: 'bold' }}
                      />
                    </Card>
                  )}

                  {/* Raw data (collapsible) */}
                  <Divider />
                  <details style={{ marginTop: 16 }}>
                    <summary style={{ cursor: 'pointer', color: '#1890ff' }}>
                      Rohdaten anzeigen (für Entwickler)
                    </summary>
                    <pre style={{ background: '#f5f5f5', padding: 12, marginTop: 8, fontSize: '12px' }}>
                      {JSON.stringify(selectedBooking.calculation, null, 2)}
                    </pre>
                  </details>
                </div>
              ) : (
                <Empty description="Keine Kalkulation vorhanden" />
              )}
            </TabPane>
          </Tabs>
        )}
      </Drawer>

      {/* Booking Creation Modal */}
      <Modal
        title={selectedDateRange ? `Neue Buchung für ${timelineData?.adverts.find(a => a.id === selectedDateRange.advertId)?.shortTitle}` : 'Neue Buchung'}
        open={bookingModalVisible}
        onCancel={() => setBookingModalVisible(false)}
        width={800}
        footer={null}
      >
        <Form
          form={bookingForm}
          layout="vertical"
          onFinish={handleCreateBooking}
          onValuesChange={handleBookingFormChange}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Card title="Buchungsdaten" size="small" style={{ marginBottom: 16 }}>
                <Form.Item
                  label="Anreise"
                  name="checkIn"
                  rules={[{ required: true, message: 'Bitte wählen Sie ein Anreisedatum' }]}
                  getValueFromEvent={(date) => date?.format('YYYY-MM-DD')}
                  getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
                >
                  <DatePicker 
                    style={{ width: '100%' }}
                    disabledDate={(date) => date && date.isBefore(dayjs().startOf('day'))}
                    format="DD.MM.YYYY"
                  />
                </Form.Item>

                <Form.Item
                  label="Abreise"
                  name="checkOut"
                  rules={[{ required: true, message: 'Bitte wählen Sie ein Abreisedatum' }]}
                  getValueFromEvent={(date) => date?.format('YYYY-MM-DD')}
                  getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
                >
                  <DatePicker 
                    style={{ width: '100%' }}
                    disabledDate={(date) => date && date.isBefore(dayjs().startOf('day').add(1, 'day'))}
                    format="DD.MM.YYYY"
                  />
                </Form.Item>

                <Row gutter={8}>
                  <Col span={8}>
                    <Form.Item
                      label="Erwachsene"
                      name="adults"
                      rules={[{ required: true, message: 'Anzahl Erwachsene erforderlich' }]}
                    >
                      <InputNumber min={1} max={20} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="Hunde"
                      name="dogs"
                    >
                      <InputNumber min={0} max={10} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <div style={{ marginTop: '30px' }}>
                      <Button 
                        type="dashed" 
                        size="small"
                        onClick={() => {
                          const children = bookingForm.getFieldValue('children') || [];
                          bookingForm.setFieldValue('children', [...children, new Date().getFullYear() - 10]);
                        }}
                      >
                        Kind hinzufügen
                      </Button>
                    </div>
                  </Col>
                </Row>

                <Form.List name="children">
                  {(fields, { add, remove }) => (
                    <div>
                      {fields.length > 0 && <label style={{ fontSize: '14px', fontWeight: 'bold' }}>Kinder (Geburtsjahr):</label>}
                      {fields.map(({ key, name, ...restField }) => (
                        <Row key={key} gutter={8} style={{ marginBottom: 8 }}>
                          <Col span={16}>
                            <Form.Item
                              {...restField}
                              name={[name]}
                              rules={[{ required: true, message: 'Geburtsjahr erforderlich' }]}
                            >
                              <InputNumber 
                                placeholder="Geburtsjahr" 
                                min={1950} 
                                max={new Date().getFullYear()} 
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Button 
                              type="text" 
                              danger
                              onClick={() => remove(name)}
                              style={{ marginTop: '4px' }}
                            >
                              Entfernen
                            </Button>
                          </Col>
                        </Row>
                      ))}
                    </div>
                  )}
                </Form.List>

                {/* Availability Status */}
                {availabilityCheck !== null && (
                  <Alert
                    type={availabilityCheck.available ? "success" : "error"}
                    message={availabilityCheck.available ? "Zeitraum verfügbar" : "Zeitraum nicht verfügbar"}
                    description={
                      !availabilityCheck.available && availabilityCheck.conflicts ? 
                        `Konflikte: ${availabilityCheck.conflicts.length} überschneidende Buchung(en)` : 
                        undefined
                    }
                    style={{ marginTop: 16 }}
                  />
                )}
              </Card>
            </Col>

            <Col span={12}>
              <Card title="Gastdaten" size="small" style={{ marginBottom: 16 }}>
                <Form.Item
                  label="Name"
                  name="guestName"
                  rules={[{ required: true, message: 'Name des Gastes ist erforderlich' }]}
                >
                  <Input placeholder="Vor- und Nachname" />
                </Form.Item>

                <Form.Item
                  label="E-Mail"
                  name="guestEmail"
                  rules={[
                    { required: true, message: 'E-Mail-Adresse ist erforderlich' },
                    { type: 'email', message: 'Ungültige E-Mail-Adresse' }
                  ]}
                >
                  <Input placeholder="gast@example.com" />
                </Form.Item>

                <Form.Item
                  label="Telefon"
                  name="guestPhone"
                >
                  <Input placeholder="+49 123 456789" />
                </Form.Item>

                <Form.Item
                  label="Besondere Wünsche"
                  name="specialRequests"
                >
                  <TextArea rows={3} placeholder="Zusätzliche Anfragen oder Hinweise..." />
                </Form.Item>
              </Card>

              {/* Price Calculation */}
              {priceCalculation && (
                <Card title="Kostenübersicht" size="small">
                  {priceCalculation.accommodation && (
                    <Row justify="space-between" style={{ marginBottom: 8 }}>
                      <Col>Unterkunft ({priceCalculation.nights || 1} Nächte):</Col>
                      <Col><strong>{(typeof priceCalculation.accommodation.total === 'number' ? priceCalculation.accommodation.total : 0).toFixed(2)} €</strong></Col>
                    </Row>
                  )}
                  
                  {priceCalculation.cleaning_fee !== undefined && priceCalculation.cleaning_fee > 0 && (
                    <Row justify="space-between" style={{ marginBottom: 8 }}>
                      <Col>Endreinigung:</Col>
                      <Col>{(typeof priceCalculation.cleaning_fee === 'number' ? priceCalculation.cleaning_fee : 0).toFixed(2)} €</Col>
                    </Row>
                  )}

                  {priceCalculation.tourist_tax && priceCalculation.tourist_tax.total > 0 && (
                    <Row justify="space-between" style={{ marginBottom: 8 }}>
                      <Col>Kurtaxe:</Col>
                      <Col>{(typeof priceCalculation.tourist_tax.total === 'number' ? priceCalculation.tourist_tax.total : 0).toFixed(2)} €</Col>
                    </Row>
                  )}

                  {priceCalculation.pet_fee !== undefined && priceCalculation.pet_fee > 0 && (
                    <Row justify="space-between" style={{ marginBottom: 8 }}>
                      <Col>Haustiergebühr:</Col>
                      <Col>{(typeof priceCalculation.pet_fee === 'number' ? priceCalculation.pet_fee : 0).toFixed(2)} €</Col>
                    </Row>
                  )}

                  <Divider style={{ margin: '12px 0' }} />
                  
                  {priceCalculation.taxes && (
                    <>
                      <Row justify="space-between" style={{ marginBottom: 4 }}>
                        <Col>Nettobetrag:</Col>
                        <Col>{(typeof priceCalculation.taxes.net_amount === 'number' ? priceCalculation.taxes.net_amount : 0).toFixed(2)} €</Col>
                      </Row>
                      <Row justify="space-between" style={{ marginBottom: 8 }}>
                        <Col>MwSt. ({priceCalculation.taxes.vat_rate || 0}%):</Col>
                        <Col>{(typeof priceCalculation.taxes.vat_amount === 'number' ? priceCalculation.taxes.vat_amount : 0).toFixed(2)} €</Col>
                      </Row>
                    </>
                  )}

                  <Row justify="space-between" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    <Col>Gesamtbetrag:</Col>
                    <Col style={{ color: '#52c41a' }}>
                      {(() => {
                        // Calculate total from all components
                        let total = 0;
                        
                        // Add accommodation
                        if (priceCalculation.accommodation?.total) {
                          total += priceCalculation.accommodation.total;
                        }
                        
                        // Add cleaning fee
                        if (priceCalculation.cleaning_fee) {
                          total += priceCalculation.cleaning_fee;
                        }
                        
                        // Add tourist tax
                        if (priceCalculation.tourist_tax?.total) {
                          total += priceCalculation.tourist_tax.total;
                        }
                        
                        // Add pet fee
                        if (priceCalculation.pet_fee) {
                          total += priceCalculation.pet_fee;
                        }
                        
                        // If we have a direct total, use it
                        if (priceCalculation.total) {
                          total = priceCalculation.total;
                        } else if (priceCalculation.taxes?.gross_amount) {
                          total = priceCalculation.taxes.gross_amount;
                        }
                        
                        return total.toFixed(2);
                      })()} €
                    </Col>
                  </Row>

                  {/* Debug: JSON Response anzeigen */}
                  <details style={{ marginTop: 16, fontSize: '12px', color: '#666' }}>
                    <summary style={{ cursor: 'pointer' }}>JSON Response anzeigen</summary>
                    <pre style={{ 
                      marginTop: 8, 
                      padding: 8, 
                      backgroundColor: '#f5f5f5',
                      borderRadius: 4,
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}>
                      {JSON.stringify(priceCalculation, null, 2)}
                    </pre>
                  </details>
                </Card>
              )}
            </Col>
          </Row>

          <Row justify="end" style={{ marginTop: 24 }}>
            <Space>
              <Button onClick={() => setBookingModalVisible(false)}>
                Abbrechen
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                disabled={availabilityCheck === null || !availabilityCheck.available}
                loading={false}
              >
                Buchung erstellen
              </Button>
            </Space>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
}; 